import { io, Socket } from 'socket.io-client';
import { MessageReturnSchemas, type Message, type MessageReturnTypes } from 'shared/modelTypes';
import { useEffect, useState } from 'react';
import { generateUUID } from 'shared/util';
import { UserProfileSchema, type UserProfile } from 'shared/modelTypes/user';
import type { MediaNode } from 'shared/modelTypes/media';
import { toast } from 'sonner';
import { redirect } from '@tanstack/react-router';
import type { FileUpload } from 'shared/modelTypes/client';

// Note: for socket.io you need to provide the domain and path separately.
// In production we use the url https://domain.com/api
const url = new URL(import.meta.env.VITE_WEBSOCKET_URL);
const origin = url.origin;
const basePath = url.pathname.replace(/\/$/, '');
const path = `${basePath}/socket.io`;

export let socket: Socket | undefined;
const messageResolvers = new Map<string, (v: unknown) => void>();

export function initSocket() {
	const token = getToken()?.token;

	if (socket) {
		socket.disconnect();
		messageResolvers.clear();
		console.log('disconnecting old socket');
	}

	socket = io(origin, {
		transports: ['websocket'],
		path: path,
		auth: {
			token,
		},
	});

	socket.on('connect', () => {
		console.log('@@@ socket connected');
	});
	socket.on('disconnect', () => {
		console.log('@@@ socket disconnected');
	});
	socket.on('error', (error) => {
		console.log('@@@ socket error', error);
	});
	socket.on('reconnect', () => {
		console.log('@@@ socket reconnected');
	});
	socket.on('reconnect_error', (error) => {
		console.log('@@@ socket reconnect error', error);
	});
	socket.on('reconnect_failed', () => {
		console.log('@@@ socket reconnect failed');
	});
	socket.on('connect_error', (error) => {
		console.log('@@@ socket connect error', error);
	});

	socket.onAny((eventName: string, payload: unknown) => {
		const resolver = messageResolvers.get(eventName);
		if (resolver) {
			console.log(payload);

			// @ts-expect-error untyped logic
			if (payload && payload.success === false && payload.message === 'not authenticated') {
				console.log('not authenticated...', eventName, payload);
				if (getToken()) {
					toast('Not authenticated, please sign in.');
				}
				_setToken(undefined);
				window.dispatchEvent(new Event('token-storage')); // Ensure updates across tabs
				// router.navigate({ to: '/login' });

				resolver(payload);
				messageResolvers.delete(eventName);

				console.log('redirecting to login');
				throw redirect({ to: '/login' });
			} else {
				resolver(payload);
				messageResolvers.delete(eventName);
			}
		}
	});

	socket.connect();
}

initSocket();

export function emitMessage<T extends Message>(
	msg: T,
): MessageReturnTypes[T['kind']] extends null ? null : Promise<MessageReturnTypes[T['kind']]> {
	const __message_id = generateUUID();
	const returnSchema = MessageReturnSchemas[msg.kind];
	if (returnSchema) {
		// This message expects return data, so return a promise that resolves with the data
		const p = new Promise<unknown[T['kind']]>((resolve) => {
			messageResolvers.set(__message_id, resolve);
			socket?.emit('msg', { ...msg, __message_id });
		});

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return p as any;
	} else {
		// No return data expected, fire and forget
		socket?.emit('msg', { ...msg, __message_id: __message_id, token: getToken() });
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return null as any;
	}
}

export function getToken(): UserProfile | undefined {
	const token = localStorage.getItem('ubgames-jwt');
	if (!token) return undefined;

	// TODO: consider validating the token shape
	try {
		const rawToken = JSON.parse(token);
		const parsedToken = UserProfileSchema.safeParse(rawToken);
		if (!parsedToken.success) {
			console.error('Invalid token', parsedToken.error);
			return undefined;
		}
		return parsedToken.data;
	} catch (e: unknown) {
		console.error(e);
		return undefined;
	}
}

function _setToken(token: UserProfile | undefined) {
	console.log('setToken');
	console.log(token);
	localStorage.setItem('ubgames-jwt', token ? JSON.stringify(token) : '');
}

export function useToken() {
	const [token, setTokenState] = useState(() => getToken());

	useEffect(() => {
		const handleStorage = () => {
			setTokenState(getToken());
		};

		window.addEventListener('token-storage', handleStorage);
		return () => window.removeEventListener('storage', handleStorage);
	}, []);

	function setToken(token: UserProfile | undefined) {
		_setToken(token);
		setTokenState(token);
		window.dispatchEvent(new Event('token-storage')); // Ensure updates across tabs
	}

	return { token, setToken };
}

export function useIsAdmin() {
	const { token } = useToken();
	return token?.role === 'admin';
}

function getTokenHeader() {
	const token = getToken()?.token;
	if (!token) return undefined;
	return { Authorization: `Bearer ${token}` };
}

export async function uploadFile(file: File, folderId: string | null): Promise<{ success: boolean; media?: MediaNode }> {
	const url = import.meta.env.VITE_WEBSOCKET_URL + '/upload-file';

	const formData = new FormData();
	formData.append('file', file);
	if (folderId) {
		formData.append('folderId', folderId);
	}
	const res = (await fetch(url, {
		method: 'POST',
		body: formData,
		headers: getTokenHeader(),
	}).then((res) => res.json())) as { success: boolean; media?: MediaNode };
	console.log({ res });

	return res;
}

// IS this the correct place for this function?
export function findDuplicateKeysById(arr: { key: string; value?: string | null }[]): number[] {
	const duplicates: number[] = [];
	if (!arr) return [];
	for (let i = 0; i < arr.length; i++) {
		const currentKey = arr[i].key.toUpperCase();

		for (let j = 0; j < arr.length; j++) {
			if (i !== j && arr[j].key.toUpperCase() === currentKey) {
				if (!duplicates.includes(i)) {
					duplicates.push(i);
				}
				break;
			}
		}
	}

	return duplicates;
}

export function getAvatarUrl(eventId: string, teamId: string, avatar: FileUpload) {
	const url = `https://${import.meta.env.VITE_CDN_GAME}/${eventId}/${teamId}/${avatar.id}.${avatar.filename.split('.').pop()}`;
	return url;
}
